import { SessionTypes } from "@walletconnect/types"
import { Divider, message } from "antd"
import { QRCodeCanvas } from "qrcode.react"
import React, { useState } from "react"

import CopyText from "@/components/CopyText"
import Drag from "@/components/Drag"
import PageLayout from "@/components/Layout"
import { Link } from "@/config"
import requests from "@/requests/requests"
import usePay, { WalletType } from "@/store/payment"
import useScreenLoading from "@/store/screenLoading"
import { AlignCenter } from "@/styles/CssElement"
import { desensitizeString } from "@/utils/utils"

import BroearnWalletModal, { BroearnWalletModalMethod } from "./broearnWalletModal"
import Connect from "./connect"
import Disconnect from "./disconnect"
import EndTime from "./endTime"
import InsufficientBalanceModal from "./InsufficientBalanceModal"
import Payment from "./payment"
import ResultModal from "./ResultModal"
import SendEmailModal from "./SendEmailModal"
import WalletConnect from "./WalletConnect"

import "./style.less"

interface LoopProps {
    onPaid: () => void
    onPaying: () => void
    onFailed: () => void
}

export function Loop({
    onPaid,
    onFailed,
    onPaying
}: LoopProps) {
    const duration = 2_000
    const setLoopOrder = usePay(s => s.setLoopOrder)
    const Polling = () => {
        requests.order().then(o => {
            const { need_confirms, already_confirms, mode, status, time_expire } = o
            setLoopOrder(o)

            if (!["PAID", "TO-BE-PAID"].includes(status) || time_expire < Date.now() / 1000) {
                onFailed()
                return
            }

            // TODO: listening status to render the trading and qrcode success when mode not mix and contract,
            if (!["mix", "contract"].includes(mode) && need_confirms > 0) {
                if (need_confirms > already_confirms) {
                    console.log("pay address is paying")
                    onPaying()
                    return
                }

                if (need_confirms >= already_confirms) {
                    console.log("pay address is paid")
                    onPaid()
                    return
                }
            }

            switch (status) {
                case "TO-BE-PAID":
                    setTimeout(
                        Polling,
                        duration
                    )
                    break
                case "PAID":
                    console.log("paid")
                    onPaid()
                    break
                default:
                    break
            }
        }).catch(() => {
            setTimeout(
                Polling,
                duration
            )
        })
    }

    React.useEffect(() => {
        Polling()
    }, [])

    return <React.Fragment></React.Fragment>
}

function useLoading() {
    const [metamask, setmetamask] = React.useState(false)
    const [walletconnect, setwalletconnect] = React.useState(false)
    const [tronLink, settronLink] = React.useState(false)
    const [broearnWallet, setbroearnWallet] = React.useState(false)
    const [okx, setokx] = React.useState(false)
    const [trust, settrust] = useState<boolean>(false)

    return {
        okx,
        metamask,
        setmetamask,
        walletconnect,
        setwalletconnect,
        setokx,
        tronLink,
        settronLink,
        broearnWallet,
        setbroearnWallet,
        trust,
        settrust
    }
}

const Cashier: React.FC = () => {
    const setScreenLoading = useScreenLoading(s => s.setScreenLoading)
    const BroearnWalletRef = React.useRef<BroearnWalletModalMethod>(null)
    const order = usePay(s => s.order)
    const goods = usePay(s => s.goods)
    const loopOrder = usePay(s => s.loopOrder)
    const Loading = useLoading()
    const [modalType, setModalType] = React.useState("")
    const [paymentStatus, setPaymentStatus] = useState({
        title: "Paying",
        icon: require("@/assets/paying.svg")
    })
    const [wt, setWt] = React.useState<WalletType>("metamask")

    const startTrading = () => {
        setPaymentStatus({
            title: "Trading",
            icon: require("@/assets/trading.svg")
        })
    }

    const onTradingSuccess = () => {
        setPaymentStatus({
            title: "Paid",
            icon: require("@/assets/paid.svg")
        })
    }

    const onTradingFailed = () => {
        setPaymentStatus({
            title: "Paying",
            icon: require("@/assets/paying.svg")
        })
    }

    const openResultModal = () => {
        setModalType("paymentResult")
    }

    const onCheck = (walletType: WalletType) => {
        setWt(walletType)
        new Connect(
            order,
            Loading[`set${walletType}`]
        )[walletType](BroearnWalletRef.current!).then(({
            address,
            session
        }: {
            address: string
            session?: SessionTypes.Struct
        }) => {
            console.log("connected address----->", address)
            new Payment(
                address,
                order,
                startTrading
            )[walletType](session!).then(() => {
                Loading[`set${walletType}`](false)
                onTradingSuccess()
            }).catch(err => {
                console.error("payment error----->", err)
                if (err) {
                    message.open({
                        content: typeof err === "string" ? err : err.message,
                        icon: <img src={require("@/assets/warning.png")} />
                    })
                }

                Loading[`set${walletType}`](false)
                onTradingFailed()
            }).finally(() => {
                setScreenLoading(false)
            })
        }).catch(err => {
            console.log("connect err----->", err)
            Loading[`set${walletType}`](false)
            if (err?.code === -1995) {
                setModalType("insufficientBalance")
            }
            else {
                message.open({
                    content: err?.message,
                    icon: <img src={require("@/assets/warning.png")} />
                })
            }
        })
    }

    const closeModal = () => {
        setModalType('')
    }

    const tryAgain = () => {
        closeModal()
        onCheck(wt)
    }

    const changeAddressTryagain = (wt: WalletType) => {
        new Disconnect(
            order,
            Loading[`set${wt}`]
        )[wt]().then(() => {
            console.log("disconnect success!")
            closeModal()
            onCheck(wt)
        })
    }

    const endCallback = () => {
        setModalType("paymentResult")
        onTradingSuccess()
    }

    const useDisable = React.useCallback((key: WalletType) => {
        if (Loading.metamask ||
            Loading.tronLink ||
            Loading.walletconnect ||
            Loading.broearnWallet ||
            Loading.okx
        ) {
            if (Loading[key]) {
                return false
            }
            return true
        }
        return false
    }, [Loading])

    const WalletsElement = React.useMemo(() => {
        const chainType = order.chain.toLocaleLowerCase();
        let wallets: React.ReactNode[] = [
            <Drag
                loading={Loading.okx}
                disabled={useDisable("okx")}
                key="okx"
            >
                <div
                    className="cashier-wallet"
                    onClick={onCheck.bind(void 0, "okx")}
                >
                    <img
                        className="cashier-wallet-icon"
                        src={require("@/assets/okx.webp")}
                    />
                    <span>OKX Wallet</span>
                </div>
            </Drag>,
            <Drag
                loading={Loading.broearnWallet}
                disabled={useDisable("broearnWallet")}
                key="broearnwallet"
            >
                <div
                    key="broearnwallet"
                    className="cashier-wallet"
                    onClick={onCheck.bind(void 0, "broearnWallet")}>
                    <img
                        className="cashier-wallet-icon"
                        src={require("@/assets/wallet.png")} />
                    <span>Broearn Wallet</span>
                </div>
            </Drag>,
            <Drag
                loading={Loading.trust}
                disabled={useDisable("trust")}
                key="trust"
            >
                <div
                    className="cashier-wallet"
                    onClick={onCheck.bind(void 0, "trust")}
                >
                    <img
                        className="cashier-wallet-icon"
                        src={require("@/assets/trust.svg")}
                    />
                    <span>Trust Wallet</span>
                </div>
            </Drag>
        ]

        if (order) {
            switch (order.chain) {
                case "Tron":
                    wallets.pop()
                    wallets.unshift(
                        <Drag
                            loading={Loading.tronLink}
                            disabled={useDisable("tronLink")}
                            key="tronLink"
                        >
                            <div
                                className="cashier-wallet"
                                onClick={onCheck.bind(void 0, "tronLink")}>
                                <img
                                    className="cashier-wallet-icon"
                                    src={require("@/assets/tronlink.png")} />
                                <span>Tron Link</span>
                            </div>
                        </Drag>
                    )
                    break
                case "PUT":
                    wallets = [
                        <Drag
                            loading={Loading.broearnWallet}
                            disabled={useDisable("broearnWallet")}
                            key="broearnwallet"
                        >
                            <div
                                key="broearnwallet"
                                className="cashier-wallet"
                                onClick={onCheck.bind(void 0, "broearnWallet")}>
                                <img
                                    className="cashier-wallet-icon"
                                    src={require("@/assets/wallet.png")} />
                                <span>Broearn Wallet</span>
                            </div>
                        </Drag>
                    ]
                    break
                default:
                    wallets.unshift([
                        <Drag
                            loading={Loading.metamask}
                            disabled={useDisable("metamask")}
                            key="metamask"
                        >
                            <div
                                key="metamask"
                                className="cashier-wallet"
                                onClick={onCheck.bind(void 0, "metamask")}
                            >
                                <img
                                    className="cashier-wallet-icon"
                                    src={require("@/assets/metamask.png")}
                                />
                                <span>Metamask</span>
                            </div>
                        </Drag>,
                    ])
                    break
            }
        }

        if (chainType === "put") {
            wallets.push(
                <Drag
                    loading={Loading.walletconnect}
                    disabled={useDisable("walletconnect")}
                    key="walletconnect"
                >
                    <div
                        key="walletconnect"
                        className="cashier-wallet"
                        onClick={onCheck.bind(void 0, "walletconnect")}>
                        <img
                            className="cashier-wallet-icon"
                            src={require("@/assets/wallet_connect.png")} />
                        <span>WalletConnect</span>
                    </div>
                </Drag>
            )
        }

        if (!["put", "tron"].includes(order.chain.toLocaleLowerCase())) {
            wallets.push(
                <WalletConnect
                    key="walletConnect"
                    disable={useDisable("" as any)}
                    order={order}
                    pendingOpen={startTrading}
                    pendingClose={onTradingSuccess}
                    paymentSuccess={() => {
                        onTradingSuccess()
                    }}
                />
            )
        }

        return wallets
    }, [order, Loading])

    if (!order.trade_no) {
        return null
    }

    return (
        <PageLayout>
            <div className="cashier">
                <AlignCenter className="header">
                    <img
                        className="header-icon"
                        src={goods.app_icon || require("@/assets/Merchant.png")}
                    />
                    <div className="header-title">{goods.app_name}</div>
                </AlignCenter>
                <div className="cashier-body-prices">{order.amount} <span>{order.token}</span></div>
                <div className="cashier-body-network">Network: {order.chain}</div>
                {order.contract !== order.token && <div className="cashier-body-contract">Contract: {order.contract}</div>}
                <div className="paymentWrapper">
                    <div className="payment">
                        {order.mode !== "contract" && (
                            <div className="payment-qrCode">
                                <QRCodeCanvas
                                    level="H"
                                    value={
                                        order.chain === "PUT" ?
                                            order.token === "PUT" ?
                                                `put:${order.pay_address}?amount=${order.amount}&network=puttest` :
                                                `put:${order.pay_address}?contractAddress=${order.token}&decimal=${order.decimal}&value=${order.amount}&network=puttest` :
                                            order.pay_address
                                    }
                                    size={114}
                                    imageSettings={{
                                        src: goods.app_icon || require("@/assets/logo.png"),
                                        width: 24,
                                        height: 24,
                                        excavate: true
                                    }}
                                />
                                {!["mix", "contract"].includes(order.mode) && paymentStatus.title === "Trading" && (
                                    <div className="payment-qrCode-mask">
                                        <img src={require("@/assets/scan.svg")} />
                                    </div>
                                )}
                            </div>
                        )}
                        <div className={`payment-info ${order.mode === "contract" ? " payment-content" : ""}`}>
                            <div className="address-title">{order.mode === "contract" ? "Payee contract address" : "Payee wallet address"}</div>
                            {order.mode === "contract" ? (
                                <div className="payment-info-address">{desensitizeString(order?.contract_pay_address, 6)}</div>
                            ) : (
                                <CopyText
                                    className="payment-info-address"
                                    copyable={{ text: order?.pay_address }}
                                >
                                    <span className="payment-info-address-value">{order?.pay_address}</span>
                                </CopyText>
                            )}
                            <div>If you have already paid, please wait for on-chain confirmation to complete</div>
                            {/* <div>We'll notify you when your payment status changes</div> */}
                            {/* <div className="payment-info-email" onClick={() => { setModalType("sendEmail") }}>
                                <span>Leave your email</span>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 8H12M12 8L9 5M12 8L9 11" stroke="currentColor" />
                                </svg>
                            </div> */}
                        </div>
                    </div>
                    <div className="status">
                        <div className="status-card">
                            <EndTime create={order.create_time} end={order.time_expire} endCallback={endCallback} />
                        </div>
                        <div className="status-card">
                            <img
                                className={`status-card-trading${paymentStatus.title === "Trading" ? " trading-animation" : ""}`}
                                src={paymentStatus.icon}
                            />
                            <div className="status-card-content trading">
                                {paymentStatus.title}
                            </div>
                            <div className="trading-status">
                                <div className={`trading-status-item${paymentStatus.title === "Paying" ? " trading-status-item-active" : ""}`}></div>
                                <div className={`trading-status-item${paymentStatus.title === "Trading" ? " trading-status-item-active" : ""}`}></div>
                                <div className={`trading-status-item${paymentStatus.title === "Paid" ? " trading-status-item-active" : ""}`}></div>
                            </div>
                        </div>
                    </div>
                </div>
                <Divider className="divider">Or choose a wallet connect</Divider>
                <div className={`cashier-body-wallets${WalletsElement.length > 4 ? " cashier-body-gridLayout" : ""}`}>
                    {WalletsElement}
                </div>
                <div className="powerBy">
                    Payments processed by ReelPay. Be sure you trust the merchant listed, as transactions are final and refunds are handled by the merchant
                    <a target="_blank" href={`${Link}policy/2`}> See full disclosures </a>
                    and
                    <a target="_blank" href={`${Link}policy/1`}> Privacy Policy </a>
                </div>
            </div>
            <BroearnWalletModal ref={BroearnWalletRef} />
            <Loop onPaying={startTrading} onPaid={openResultModal} onFailed={openResultModal} />
            {modalType === "insufficientBalance" && (
                <InsufficientBalanceModal
                    visible={true}
                    tryAgain={tryAgain}
                    canSwitch={order.chain !== "Tron"}
                    switchAddress={() => changeAddressTryagain(wt)}
                    onClose={closeModal}
                />
            )}
            {modalType === "sendEmail" && <SendEmailModal visible={true} onClose={closeModal} />}
            {modalType === "paymentResult" && (
                <ResultModal
                    amount={`${loopOrder.amount} ${loopOrder.token}`}
                    mode={loopOrder.status === "PAID" ? "successful" : "failed"}
                />
            )}
        </PageLayout >
    )
}

export default Cashier
